import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, Loader2, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

/**
 * One-time historical backfill of produced qty per (SO, Line).
 * Admin-only. Downloads a pre-filled Excel template listing every SO × every Line row so the
 * user just needs to fill the `Produced Till Date` column. Uploads back and POSTs to
 * /api/output/backfill which upserts a single output record per (date=cutoff, line, SO).
 */
export default function BackfillButton({ orders, lines, onDone }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [cutoff, setCutoff] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = () => {
    // One row per SO × each defined line so the admin just needs to fill the Produced column.
    const templateRows = [];
    orders.forEach((o) => {
      lines.forEach((l) => {
        templateRows.push({
          "SO Number": o.order_number,
          "Buyer": o.buyer || "",
          "BBS Style": o.bbs_style || o.style || "",
          "Line Name": l.name,
          "Ord Qty": o.order_qty || 0,
          "Produced Till Date": 0, // fill in
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(templateRows);
    ws["!cols"] = [{ wch: 20 }, { wch: 24 }, { wch: 20 }, { wch: 14 }, { wch: 10 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Backfill");
    XLSX.writeFile(wb, `opsiq-produced-backfill-template-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`Template downloaded — ${templateRows.length} rows`);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
      // Keep only rows where Produced Till Date > 0
      const parsed = data
        .map((r) => ({
          order_number: String(r["SO Number"] || r["SO"] || "").trim(),
          line_name: String(r["Line Name"] || r["Line"] || "").trim(),
          produced_qty: Number(String(r["Produced Till Date"] ?? r["Produced"] ?? 0).toString().replace(/,/g, "")) || 0,
        }))
        .filter((r) => r.order_number && r.line_name && r.produced_qty > 0);
      setRows(parsed);
      setResult(null);
      toast.success(`Parsed ${parsed.length} row${parsed.length === 1 ? "" : "s"} with produced qty > 0`);
    } catch (err) {
      toast.error("Couldn't parse the file. Please use the downloaded template.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (rows.length === 0) { toast.error("No rows to submit — download the template, fill it in, then upload."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/output/backfill", { cutoff_date: cutoff, rows });
      setResult(data);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-brand text-brand hover:bg-brand-soft/40" data-testid="backfill-btn">
        <Upload className="h-4 w-4 mr-2" /> Backfill historical qty
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setRows([]); setResult(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle>Backfill produced qty (one-time)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-[13px]">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-amber-900">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  Use this only when Ops IQ goes live mid-way through existing orders. Every filled row becomes a single output record dated <b>{cutoff}</b> so real-time entries starting today roll up correctly.
                  <br />Uploading the same file twice is safe — existing backfill rows on that date are overwritten (not doubled).
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div className="sm:col-span-1">
                <label className="text-[11.5px] font-semibold text-slate-600 uppercase tracking-wide">Cutoff date</label>
                <Input type="date" value={cutoff} onChange={(e) => setCutoff(e.target.value)} className="mt-1 h-10" data-testid="backfill-cutoff" />
              </div>
              <div className="sm:col-span-2 flex items-end gap-2 flex-wrap">
                <Button variant="outline" onClick={downloadTemplate} className="h-10" data-testid="backfill-download-template"><Download className="h-4 w-4 mr-2" /> Download template</Button>
                <input type="file" ref={fileRef} accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-10" data-testid="backfill-upload"><Upload className="h-4 w-4 mr-2" /> Upload filled file</Button>
              </div>
            </div>

            {rows.length > 0 && !result && (
              <div className="rounded-md border border-slate-200 max-h-64 overflow-y-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-slate-50 sticky top-0"><tr className="text-left"><th className="p-2">SO #</th><th className="p-2">Line</th><th className="p-2 text-right">Produced</th></tr></thead>
                  <tbody>
                    {rows.slice(0, 200).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-2 font-mono">{r.order_number}</td>
                        <td className="p-2">{r.line_name}</td>
                        <td className="p-2 text-right font-mono">{r.produced_qty.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 200 && <div className="p-2 text-[11px] text-slate-500 text-center">+ {rows.length - 200} more rows</div>}
              </div>
            )}

            {result && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4">
                <div className="flex items-center gap-2 font-semibold text-emerald-900 text-[14px]"><CheckCircle2 className="h-5 w-5" /> Backfill complete</div>
                <div className="mt-2 grid grid-cols-3 gap-3 text-center">
                  <div><div className="font-heading font-bold text-2xl text-emerald-700">{result.applied}</div><div className="text-[10.5px] uppercase font-semibold text-slate-500">Applied</div></div>
                  <div><div className="font-heading font-bold text-2xl text-blue-700">{result.orders_updated}</div><div className="text-[10.5px] uppercase font-semibold text-slate-500">SOs Updated</div></div>
                  <div><div className="font-heading font-bold text-2xl text-slate-500">{result.skipped}</div><div className="text-[10.5px] uppercase font-semibold text-slate-500">Skipped</div></div>
                </div>
                {(result.errors || []).length > 0 && (
                  <details className="mt-3 text-[11.5px]">
                    <summary className="cursor-pointer text-red-700 font-semibold">{result.errors.length} row error(s) — click to view</summary>
                    <ul className="mt-1 list-disc list-inside text-slate-600">
                      {result.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {!result && (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy || rows.length === 0} className="bg-brand hover:bg-brand-dark text-white" data-testid="backfill-submit">
                  {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : `Submit ${rows.length} row${rows.length === 1 ? "" : "s"}`}
                </Button>
              </>
            )}
            {result && <Button onClick={() => setOpen(false)} className="bg-brand hover:bg-brand-dark text-white"><X className="h-4 w-4 mr-2" /> Close</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
